/**
 * Build sidebar navItems dynamically from the plugin UI registry.
 *
 * Replaces ~140 lines of hardcoded if/else in app.tsx with a manifest-driven
 * approach: each plugin declares navGroup/navOrder in its striker.plugin.json,
 * and this function assembles the sidebar tree.
 */
import type { ShellNavItem } from '@striker/ui-shell';
import type { GeneratedUiRoute, GeneratedNavGroup } from './pluginUiRegistry.generated';

interface BuildNavItemsParams {
  routes: GeneratedUiRoute[];
  navGroups: GeneratedNavGroup[];
  canUseTenantPlugins: boolean;
  moduleOn: (code: string) => boolean;
  fitsCategory: (moduleCode: string) => boolean;
  /** Map of /tenant/path → true for routes that exist in the registry */
  hasRoute: (path: string) => boolean;
}

/**
 * Normalize a plugin route path to a /tenant/... path.
 * Plugin paths are relative (e.g. "menu/products") or absolute (e.g. "/hello").
 */
function tenantPath(routePath: string): string {
  return `/tenant/${routePath.replace(/^\/+/, '')}`;
}

/**
 * Build the sidebar navigation tree from the plugin UI registry.
 *
 * Algorithm:
 * 1. Partition routes into "grouped" (has navGroup) and "standalone" (no navGroup).
 * 2. For each group, find a "parent route" (a route whose navGroup equals its own key)
 *    or use a virtual group from navGroups.
 * 3. Build children from grouped routes, sorted by navOrder.
 * 4. Build top-level items from standalone routes + group parents, sorted by navOrder.
 */
export function buildNavItemsFromRegistry({
  routes,
  navGroups,
  canUseTenantPlugins,
  moduleOn,
  fitsCategory,
  hasRoute,
}: BuildNavItemsParams): ShellNavItem[] {
  if (!canUseTenantPlugins) return [];

  // Filter routes: must have a navItem, module visible, and fit category
  const visibleRoutes = routes.filter((r) => {
    if (!r.navItem) return false;
    if (!moduleOn(r.moduleCode)) return false;
    if (!fitsCategory(r.moduleCode)) return false;
    if (!hasRoute(tenantPath(r.path))) return false;
    return true;
  });

  // Partition into grouped and standalone
  const grouped = visibleRoutes.filter((r) => r.navItem!.navGroup);
  const standalone = visibleRoutes.filter((r) => !r.navItem!.navGroup);

  // Build group map: groupKey → { parent route or virtual group, children }
  const groupChildren = new Map<string, GeneratedUiRoute[]>();
  for (const r of grouped) {
    const g = r.navItem!.navGroup!;
    if (!groupChildren.has(g)) groupChildren.set(g, []);
    groupChildren.get(g)!.push(r);
  }

  // Build group parents
  const groupParentItems: ShellNavItem[] = [];
  for (const [groupKey, children] of groupChildren) {
    // Check if there's a parent route (a route whose navGroup equals its own key)
    const parentRoute = visibleRoutes.find(
      (r) => r.navItem!.navGroup === groupKey && r.navItem!.key === groupKey,
    );

    if (parentRoute) {
      // Parent is a real route — build children from other routes in the group
      const childItems: ShellNavItem[] = children
        .filter((r) => r.navItem!.key !== groupKey) // exclude parent itself
        .sort((a, b) => (a.navItem!.navOrder ?? 0) - (b.navItem!.navOrder ?? 0))
        .map((r) => ({
          key: r.navItem!.key,
          label: r.navItem!.label,
          path: r.navItem!.path || tenantPath(r.path),
          icon: r.navItem!.icon,
          desktopOnly: r.navItem!.desktopOnly,
        }));

      groupParentItems.push({
        key: parentRoute.navItem!.key,
        label: parentRoute.navItem!.label,
        path: parentRoute.navItem!.path || tenantPath(parentRoute.path),
        icon: parentRoute.navItem!.icon,
        children: childItems.length > 0 ? childItems : undefined,
      });
    } else {
      // Virtual group — find metadata in navGroups
      const virtualGroup = navGroups.find((g) => g.key === groupKey);
      if (!virtualGroup) continue;

      // Check module visibility for the group's moduleCode
      const groupModuleCode = virtualGroup.moduleCode;
      if (groupModuleCode && !moduleOn(groupModuleCode)) continue;
      if (groupModuleCode && !fitsCategory(groupModuleCode)) continue;

      const childItems: ShellNavItem[] = children
        .sort((a, b) => (a.navItem!.navOrder ?? 0) - (b.navItem!.navOrder ?? 0))
        .map((r) => ({
          key: r.navItem!.key,
          label: r.navItem!.label,
          path: r.navItem!.path || tenantPath(r.path),
          icon: r.navItem!.icon,
          desktopOnly: r.navItem!.desktopOnly,
        }));

      if (childItems.length === 0) continue;

      groupParentItems.push({
        key: virtualGroup.key,
        label: virtualGroup.label,
        path: childItems[0]?.path || '/tenant',
        icon: virtualGroup.icon,
        children: childItems,
      });
    }
  }

  // Build standalone items
  const standaloneItems: ShellNavItem[] = standalone.map((r) => ({
    key: r.navItem!.key,
    label: r.navItem!.label,
    path: r.navItem!.path || tenantPath(r.path),
    icon: r.navItem!.icon,
  }));

  // Merge and sort by navOrder
  // For group parents, use the parent route's navOrder or the virtual group's order
  const allItems: ShellNavItem[] = [...standaloneItems, ...groupParentItems];

  // Build a navOrder lookup for sorting
  const orderMap = new Map<string, number>();
  for (const r of standalone) {
    orderMap.set(r.navItem!.key, r.navItem!.navOrder ?? 999);
  }
  for (const [groupKey] of groupChildren) {
    const parentRoute = visibleRoutes.find(
      (r) => r.navItem!.navGroup === groupKey && r.navItem!.key === groupKey,
    );
    if (parentRoute) {
      orderMap.set(groupKey, parentRoute.navItem!.navOrder ?? 999);
    } else {
      const vg = navGroups.find((g) => g.key === groupKey);
      orderMap.set(groupKey, vg?.order ?? 999);
    }
  }

  allItems.sort((a, b) => (orderMap.get(a.key) ?? 999) - (orderMap.get(b.key) ?? 999));

  return allItems;
}
